import {
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { NavLink, Text } from '@mantine/core';
import clsx from 'clsx';

import { GoogleFontIcon } from '../../icons/GoogleFontIcon';
import { TextWithTooltip } from '../../tooltip/TextWithTooltip';

import styles from './navigationItem.module.scss';

export type NavigationItemProps = {
  active?: boolean;
  collapsed: boolean;
  collapsedTooltipWidth?: string;
  iconName: string;
  isDetailsPageSection?: boolean;
  label: string;
  lineClamp?: number;
  sublabels?: (string | ReactNode)[];
  tooltip?: ReactNode;
  onClick?: () => void;
  onClose?: () => void;
  isPending?: boolean;
};

function NavigationItemContent({
  active = false,
  iconName,
  isDetailsPageSection,
  label,
  lineClamp = 2,
  sublabels,
  collapsed,
  onClick,
  onClose,
  onMouseEnter,
  isPending = false,
  ref,
}: Omit<NavigationItemProps, 'collapsedTooltipWidth'> & {
  label: string;
  iconName: string;
  onMouseEnter?: () => void;
  ref?: Ref<HTMLParagraphElement>;
}) {
  const { ref: navLinkRef } = useScrollIntoView({ isPending, active });
  const hasSublabels = sublabels != null && sublabels.length > 0;

  return (
    <NavLink
      ref={navLinkRef}
      component="button"
      label={
        !collapsed && (
          <>
            <Text
              ref={ref}
              component="span"
              inherit
              lineClamp={hasSublabels ? undefined : (lineClamp ?? 2)}
              onMouseEnter={onMouseEnter}
            >
              {hasSublabels ? (
                <span className={styles['label-line']}>{label}</span>
              ) : (
                label
              )}
            </Text>
            {hasSublabels &&
              sublabels.map((line, i) => (
                <span key={i} className={styles['sub-label']}>
                  {line}
                </span>
              ))}
          </>
        )
      }
      leftSection={<GoogleFontIcon solid small name={iconName} />}
      rightSection={
        !collapsed && onClose ? (
          <GoogleFontIcon
            className={styles['close-icon']}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            solid
            small
            name="close"
          />
        ) : undefined
      }
      classNames={{
        root: clsx(
          styles['root'],
          collapsed && styles['root--collapsed'],
          active && styles['root--active'],
          !active &&
            isDetailsPageSection &&
            styles['root--details-page-section'],
          onClick != null && styles['root--clickable'],
          isDetailsPageSection && hasSublabels && styles['root--multiline']
        ),
        body: styles['body'],
        section: styles['icon'],
        label: clsx(
          styles['label'],
          isDetailsPageSection && styles['label--detail']
        ),
      }}
      unstyled
      onClick={onClick}
    />
  );
}

export const NavigationItem = ({
  active,
  collapsed,
  collapsedTooltipWidth,
  iconName,
  isDetailsPageSection,
  label,
  lineClamp,
  sublabels,
  tooltip,
  onClick,
  onClose,
  isPending,
}: NavigationItemProps) => {
  const [disabled, setDisabled] = useState(true);
  const ref = useRef<HTMLParagraphElement>(null);

  const updateDisabledFromOverflow = useCallback(() => {
    const el = ref.current;
    if (el) {
      setDisabled(el.scrollHeight <= el.clientHeight + 1);
    }
  }, []);

  return (
    <TextWithTooltip
      tooltipProps={{
        position: 'right',
        label: tooltip ?? label,
        classNames: {
          tooltip: isDetailsPageSection
            ? styles['tooltip--details-page-section']
            : undefined,
        },
        width: collapsedTooltipWidth,
        disabled: !collapsed && (tooltip == null ? disabled : false),
      }}
      textProps={{ span: true }}
    >
      <NavigationItemContent
        ref={ref}
        active={active}
        collapsed={collapsed}
        iconName={iconName}
        isDetailsPageSection={isDetailsPageSection}
        key={label}
        label={label}
        lineClamp={lineClamp}
        onClick={onClick}
        onClose={onClose}
        onMouseEnter={updateDisabledFromOverflow}
        sublabels={sublabels}
        isPending={isPending}
      />
    </TextWithTooltip>
  );
};

function useScrollIntoView({
  isPending,
  active,
}: {
  isPending: boolean;
  active: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const scrollParent = getScrollParent(ref.current);
    if (active && !isPending && ref.current && scrollParent) {
      const scrollParentDOMRect = scrollParent.getBoundingClientRect();
      const navLinkDOMRect = ref.current.getBoundingClientRect();
      const cutOff =
        navLinkDOMRect.top < scrollParentDOMRect.top ||
        navLinkDOMRect.bottom > scrollParentDOMRect.bottom;

      if (cutOff) {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [isPending, active]);

  return { ref };
}

function getScrollParent(node: HTMLElement | null): HTMLElement | null {
  if (node == null) {
    return null;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return node.parentNode instanceof HTMLElement
      ? getScrollParent(node.parentNode)
      : null;
  }
}
